/**
 * Utilities for persisting component state to localStorage
 */

type CookbookState = {
  searchQuery: string;
  selectedCuisines: string[];
  selectedDifficulty: string;
  selectedDietaryRestrictions: string[];
  selectedPrepTime: string;
  selectedTotalTime: string;
  selectedOrigin: string;
  selectedMeal?: string;
  showNeedsReviewOnly?: boolean;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  currentPage: number;
  // Hash of filter state to validate page is still relevant
  filterHash: string;
};

/**
 * Create a hash of filter state to validate pagination relevance
 */
const createFilterHash = (state: Omit<CookbookState, 'currentPage' | 'filterHash'>): string => {
  return JSON.stringify({
    searchQuery: state.searchQuery,
    selectedCuisines: state.selectedCuisines.sort(),
    selectedDifficulty: state.selectedDifficulty,
    selectedDietaryRestrictions: state.selectedDietaryRestrictions.sort(),
    selectedPrepTime: state.selectedPrepTime,
    selectedTotalTime: state.selectedTotalTime,
    selectedOrigin: state.selectedOrigin,
    selectedMeal: state.selectedMeal ?? '',
    showNeedsReviewOnly: state.showNeedsReviewOnly,
    sortField: state.sortField,
    sortDirection: state.sortDirection,
  });
};

const COOKBOOK_STATE_KEY = 'mealplan-cookbook-state';
const ARCHIVE_STATE_KEY = 'mealplan-archive-state';

/**
 * Save cookbook state to localStorage
 */
export const saveCookbookState = (state: Omit<CookbookState, 'filterHash'> & { currentPage: number }): void => {
  try {
    const filterHash = createFilterHash(state);
    const stateWithHash = { ...state, filterHash };
    localStorage.setItem(COOKBOOK_STATE_KEY, JSON.stringify(stateWithHash));
  } catch (error) {
    console.warn('Failed to save cookbook state to localStorage:', error);
  }
};

/**
 * Load cookbook state from localStorage
 */
export const loadCookbookState = (): Partial<CookbookState> | null => {
  try {
    const stored = localStorage.getItem(COOKBOOK_STATE_KEY);
    if (!stored) return null;
    
    const state = JSON.parse(stored) as CookbookState;
    // Validate that this looks like a cookbook state object
    if (typeof state === 'object' && state !== null) {
      // Check if current filter state matches saved filter hash
      const currentFilterHash = createFilterHash(state);
      
      if (state.filterHash && state.filterHash === currentFilterHash) {
        return state;
      } else {
        // Filters have changed, return state but reset page to 1
        return { ...state, currentPage: 1 };
      }
    }
    return null;
  } catch (error) {
    console.warn('Failed to load cookbook state from localStorage:', error);
    return null;
  }
};

const PLANNING_LIMITS_KEY = 'mealplan-planning-limits';

/**
 * Persist the last-known future meal plan count for a user, so the nav's
 * "limit reached" state renders correctly on page load before auth restores
 * and fresh data arrives.
 */
export const savePlanningLimitsCount = (uid: string, count: number): void => {
  try {
    localStorage.setItem(PLANNING_LIMITS_KEY, JSON.stringify({ uid, count }));
  } catch (error) {
    console.warn('Failed to save planning limits to localStorage:', error);
  }
};

/**
 * Load the persisted future meal plan count along with the uid it belongs
 * to. Read synchronously before auth restores (the uid isn't known yet at
 * first render), so the caller must discard it if a different user turns
 * out to be signed in.
 */
export const loadPlanningLimitsState = (): { uid: string; count: number } | null => {
  try {
    const stored = localStorage.getItem(PLANNING_LIMITS_KEY);
    if (!stored) return null;

    const state = JSON.parse(stored);
    if (state && typeof state.uid === 'string' && typeof state.count === 'number') {
      return state;
    }
    return null;
  } catch (error) {
    console.warn('Failed to load planning limits from localStorage:', error);
    return null;
  }
};

/**
 * Clear the persisted planning limits state (on sign-out).
 */
export const clearPlanningLimitsCount = (): void => {
  try {
    localStorage.removeItem(PLANNING_LIMITS_KEY);
  } catch (error) {
    console.warn('Failed to clear planning limits from localStorage:', error);
  }
};

/**
 * Save archive state to localStorage
 */
export const saveArchiveState = (state: Omit<CookbookState, 'filterHash'> & { currentPage: number }): void => {
  try {
    const filterHash = createFilterHash(state);
    const stateWithHash = { ...state, filterHash };
    localStorage.setItem(ARCHIVE_STATE_KEY, JSON.stringify(stateWithHash));
  } catch (error) {
    console.warn('Failed to save archive state to localStorage:', error);
  }
};

/**
 * Load archive state from localStorage
 */
export const loadArchiveState = (): Partial<CookbookState> | null => {
  try {
    const stored = localStorage.getItem(ARCHIVE_STATE_KEY);
    if (!stored) return null;
    
    const state = JSON.parse(stored) as CookbookState;
    // Validate that this looks like a cookbook state object
    if (typeof state === 'object' && state !== null) {
      // Check if current filter state matches saved filter hash
      const currentFilterHash = createFilterHash(state);
      
      if (state.filterHash && state.filterHash === currentFilterHash) {
        return state;
      } else {
        // Filters have changed, return state but reset page to 1
        return { ...state, currentPage: 1 };
      }
    }
    return null;
  } catch (error) {
    console.warn('Failed to load archive state from localStorage:', error);
    return null;
  }
};